import {
    AccessTime,
    CallSplit,
    DeviceThermostat,
    DoNotDisturb,
    MailOutline,
    SupervisedUserCircleOutlined,
} from '@mui/icons-material';

import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import useAudienceData from 'domains/campaigns/components/CustomCampaign/CustomCampaignNavigation/CustomCampaignNavigationBlock/useAudienceData';
import CustomCampaignNavigationButton from 'domains/campaigns/components/CustomCampaign/CustomCampaignNavigation/CustomCampaignNavigationButton';
import { channelIcons } from 'domains/campaigns/pages/CurationsPage/utils';
import { CampaignChannelLabels, CampaignChannels, GraphNode, SplitActions } from 'domains/campaigns/types';
import { Template } from 'domains/content/types';
import useTemplates from 'hooks/queries/useTemplates';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats a delay interval string, e.g. "5 hour" → "5 hours", "1 day" → "1 day".
 * Normalises the unit to singular before applying the plural suffix so inputs
 * like "2 days" produce "2 days" (not "2 dayss").
 */
const formatDelayInterval = (interval?: string): string | undefined => {
    if (!interval) return undefined;
    const parts = interval.split(' ');
    if (parts.length < 2) return interval;
    const value = Number(parts[0]);
    const unitSingular = parts[1].replace(/s$/, '');
    return `${value} ${value > 1 ? `${unitSingular}s` : unitSingular}`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export type Props = {
    node: GraphNode;
    activeSendIndex: number;
    activeStep: CustomCampaignSteps;
    setActiveSendIndex?: (index: number) => void;
    setActiveStep?: (step: CustomCampaignSteps) => void;
    readonly: boolean;
};

const GraphNodeBlock = ({ node, activeSendIndex, activeStep, setActiveSendIndex, setActiveStep, readonly }: Props) => {
    const { currentAudience } = useAudienceData({
        segmentId: node.type === 'audience' ? node.data?.segmentId : undefined,
        cohortId: node.type === 'audience' ? node.data?.cohortId : undefined,
    });

    const isEmailSend = node.type === 'send' && node.data?.channel === CampaignChannels.EMAIL;
    const { data: templates } = useTemplates({ enabled: isEmailSend });
    const templateName =
        isEmailSend && node.data?.templateId
            ? templates?.find((t: Template) => t.id === node.data?.templateId)?.name
            : undefined;

    const navigate = (step: CustomCampaignSteps, sendIndex?: number) => {
        if (readonly) return;
        if (setActiveStep) setActiveStep(step);
        if (setActiveSendIndex && sendIndex != null) setActiveSendIndex(sendIndex);
    };

    // -----------------------------------------------------------------------
    // audience — only render once an audience has been chosen
    // -----------------------------------------------------------------------
    if (node.type === 'audience') {
        if (!node.data?.segmentId && !node.data?.cohortId) return null;
        return (
            <div data-testid="graph-node-audience">
                <CustomCampaignNavigationButton
                    aria-label="audience"
                    icon={<SupervisedUserCircleOutlined />}
                    isActive={!readonly && activeStep === CustomCampaignSteps.SELECT_AUDIENCE}
                    label="audience"
                    onClick={readonly ? undefined : () => navigate(CustomCampaignSteps.SELECT_AUDIENCE, 0)}
                    value={currentAudience?.name}
                />
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // ab_test
    // -----------------------------------------------------------------------
    if (node.type === 'ab_test') {
        return (
            <div data-testid="graph-node-ab-test">
                <CustomCampaignNavigationButton
                    aria-label="A/B test"
                    icon={<DeviceThermostat />}
                    isActive={
                        !readonly &&
                        activeStep === CustomCampaignSteps.SETUP_A_B_TEST &&
                        node.data?.sendIndex === activeSendIndex
                    }
                    label="A/B test"
                    onClick={
                        readonly ? undefined : () => navigate(CustomCampaignSteps.SETUP_A_B_TEST, node.data?.sendIndex)
                    }
                    value={node.data?.abTestConfig?.testOn ?? ''}
                />
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // time_delay
    // -----------------------------------------------------------------------
    if (node.type === 'time_delay') {
        return (
            <div data-testid="graph-node-time-delay">
                <CustomCampaignNavigationButton
                    aria-label="time delay"
                    icon={<AccessTime />}
                    isActive={
                        !readonly &&
                        activeStep === CustomCampaignSteps.SET_TIME_DELAY &&
                        node.data?.sendIndex === activeSendIndex
                    }
                    label="time delay"
                    onClick={
                        readonly ? undefined : () => navigate(CustomCampaignSteps.SET_TIME_DELAY, node.data?.sendIndex)
                    }
                    value={formatDelayInterval(node.data?.interval)}
                />
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // conditional_split
    // -----------------------------------------------------------------------
    if (node.type === 'conditional_split') {
        return (
            <div data-testid="graph-node-conditional-split">
                <CustomCampaignNavigationButton
                    aria-label="conditional split"
                    icon={<CallSplit />}
                    isActive={
                        !readonly &&
                        activeStep === CustomCampaignSteps.CONDITIONAL_SPLIT &&
                        node.data?.sendIndex === activeSendIndex
                    }
                    label="conditional split"
                    onClick={
                        readonly
                            ? undefined
                            : () => navigate(CustomCampaignSteps.CONDITIONAL_SPLIT, node.data?.sendIndex)
                    }
                />
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // send
    // -----------------------------------------------------------------------
    if (node.type === 'send') {
        const channel = node.data?.channel;
        const splitType = node.data?.splitType;
        const splitAction = node.data?.splitAction;
        const isBranchSend = !!splitType;

        // Split branch send with no action chosen yet — show disabled choose-action button
        if (isBranchSend && !splitAction) {
            return (
                <div data-testid="graph-node-send">
                    {splitType && <div>{splitType}</div>}
                    <CustomCampaignNavigationButton
                        aria-label={splitType ?? 'choose action'}
                        disabled
                        icon={<CallSplit />}
                        isActive={false}
                        label={splitType ?? 'choose action'}
                        onClick={
                            readonly
                                ? undefined
                                : () => navigate(CustomCampaignSteps.SELECT_PATH_ACTION, node.data?.sendIndex)
                        }
                    />
                </div>
            );
        }

        // Exit branch (splitAction === EXIT)
        if (splitAction === SplitActions.EXIT) {
            return (
                <div data-testid="graph-node-send">
                    {splitType && <div>{splitType}</div>}
                    <CustomCampaignNavigationButton
                        aria-label="exit curation"
                        icon={<DoNotDisturb />}
                        isActive={
                            !readonly &&
                            activeStep === CustomCampaignSteps.SELECT_PATH_ACTION &&
                            node.data?.sendIndex === activeSendIndex
                        }
                        label="exit curation"
                        onClick={
                            readonly
                                ? undefined
                                : () => navigate(CustomCampaignSteps.SELECT_PATH_ACTION, node.data?.sendIndex)
                        }
                    />
                </div>
            );
        }

        // Email send with no template selected and not a branch send — hide until configured
        if (channel === CampaignChannels.EMAIL && !node.data?.templateId && !isBranchSend) {
            return null;
        }

        const SendIcon = channel && channelIcons[channel] ? channelIcons[channel] : MailOutline;
        const channelLabel = channel ? CampaignChannelLabels[channel] ?? channel : 'send';

        return (
            <div data-testid="graph-node-send">
                {splitType && <div>{splitType}</div>}
                <CustomCampaignNavigationButton
                    aria-label={channelLabel}
                    icon={<SendIcon />}
                    isActive={
                        !readonly &&
                        (activeStep === CustomCampaignSteps.SELECT_CONTENT ||
                            activeStep === CustomCampaignSteps.SCHEDULE_CURATION) &&
                        node.data?.sendIndex === activeSendIndex
                    }
                    label={channelLabel}
                    onClick={
                        readonly
                            ? undefined
                            : () =>
                                  navigate(
                                      isBranchSend
                                          ? CustomCampaignSteps.SELECT_PATH_ACTION
                                          : CustomCampaignSteps.SELECT_CONTENT,
                                      node.data?.sendIndex
                                  )
                    }
                    value={channel === CampaignChannels.EMAIL ? templateName : node.data?.sendName}
                />
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // exit
    // -----------------------------------------------------------------------
    if (node.type === 'exit') {
        return (
            <div data-testid="graph-node-exit">
                {node.data?.splitType && <div>{node.data.splitType}</div>}
                <CustomCampaignNavigationButton
                    aria-label="exit curation"
                    icon={<DoNotDisturb />}
                    label="exit curation"
                    onClick={undefined}
                />
            </div>
        );
    }

    return null;
};

export default GraphNodeBlock;
